import { SimNPC } from './SimNPC';
import type { CardWithId } from '../../types/card';
import type { CardPoolConfig } from '../utils/CardFilter';
import type { GroupModeConfig, GroupStats, GroupId, GroupConfig, BattleConfig } from './Group';
import { createEmptyGroupStats, getGroupBattleConfig } from './Group';

/**
 * グローバルカード統計（カードID別）
 */
export interface GlobalCardStat {
  cardId: string;
  cardName: string;
  attribute: string;
  cardType: string;      // monster, magic, field, phasecard
  gamesUsed: number;     // このカードが使われた試合数
  winsWithCard: number;  // このカードを持っていて勝った試合数
}

/**
 * 日別カード統計スナップショット
 */
export interface DailyCardSnapshot {
  day: number;
  stats: Map<string, GlobalCardStat>;  // cardId -> stat
  totalGames: number;                   // その日までの総試合数
}

/**
 * シミュレーションワールド状態
 */
export interface SimWorld {
  npcs: SimNPC[];
  day: number;
  logs: string[];

  // グローバルカード統計
  globalCardStats: Map<string, GlobalCardStat>;
  dailySnapshots: DailyCardSnapshot[];
  totalGamesPlayed: number;

  // カードプール設定（除外カード等）- グループなしモード用
  cardPoolConfig?: CardPoolConfig;

  // グループモード設定
  groupModeConfig?: GroupModeConfig;

  // グループ統計（グループモード時のみ使用）
  groupStats?: Map<GroupId, GroupStats>;
}

/**
 * SimWorld作成オプション
 */
export interface CreateSimWorldOptions {
  cardPoolConfig?: CardPoolConfig;
  groupModeConfig?: GroupModeConfig;
}

/**
 * SimWorld作成
 * @param npcs 初期NPCリスト
 * @param options オプション（カードプール設定、グループモード設定）
 */
export function createSimWorld(
  npcs: SimNPC[],
  options: CreateSimWorldOptions = {}
): SimWorld {
  const { cardPoolConfig, groupModeConfig } = options;

  // グループモードが有効な場合、グループ統計を初期化
  let groupStats: Map<GroupId, GroupStats> | undefined;
  if (groupModeConfig?.enabled && groupModeConfig.groups.length > 0) {
    groupStats = new Map();
    for (const group of groupModeConfig.groups) {
      groupStats.set(group.id, createEmptyGroupStats(group.id));
    }
    // 初期人口をカウント
    for (const npc of npcs) {
      if (npc.group) {
        const stats = groupStats.get(npc.group);
        if (stats) {
          stats.population++;
          stats.aliveCount++;
        }
      }
    }
  }

  return {
    npcs,
    day: 0,
    logs: [],
    globalCardStats: new Map(),
    dailySnapshots: [],
    totalGamesPlayed: 0,
    cardPoolConfig,
    groupModeConfig,
    groupStats,
  };
}

/**
 * ログ追加（コンソール出力 + 保存）
 */
export function addLog(world: SimWorld, message: string): void {
  const logMessage = `[Day ${world.day}] ${message}`;
  world.logs.push(logMessage);
  console.log(logMessage);
}

/**
 * デュエル結果をグローバル統計に反映
 * @param world ワールド
 * @param winner 勝者NPC
 * @param loser 敗者NPC
 */
export function recordDuelResult(
  world: SimWorld,
  winner: SimNPC,
  loser: SimNPC
): void {
  world.totalGamesPlayed++;

  // グループ統計の更新（グループモード時）
  if (world.groupStats && winner.group && loser.group) {
    const winnerStats = world.groupStats.get(winner.group);
    const loserStats = world.groupStats.get(loser.group);

    if (winnerStats) {
      winnerStats.totalWins++;
      winnerStats.winsVsGroup.set(
        loser.group,
        (winnerStats.winsVsGroup.get(loser.group) || 0) + 1
      );
    }

    if (loserStats) {
      loserStats.totalLosses++;
      loserStats.lossesVsGroup.set(
        winner.group,
        (loserStats.lossesVsGroup.get(winner.group) || 0) + 1
      );
    }
  }

  // 勝者のデッキのカードを記録（勝利）
  // Map<cardId, card>でO(1)アクセス（パフォーマンス最適化）
  const winnerCardMap = new Map<string, CardWithId>();
  for (const card of winner.deck) {
    if (!winnerCardMap.has(card.id)) {
      winnerCardMap.set(card.id, card);
    }
  }

  for (const [cardId, card] of winnerCardMap) {
    let stat = world.globalCardStats.get(cardId);
    if (!stat) {
      stat = {
        cardId,
        cardName: card.name,
        attribute: card.attribute || 'なし',
        cardType: card.type || 'monster',
        gamesUsed: 0,
        winsWithCard: 0,
      };
      world.globalCardStats.set(cardId, stat);
    }
    stat.gamesUsed++;
    stat.winsWithCard++;
  }

  // 敗者のデッキのカードを記録（敗北）
  const loserCardMap = new Map<string, CardWithId>();
  for (const card of loser.deck) {
    if (!loserCardMap.has(card.id)) {
      loserCardMap.set(card.id, card);
    }
  }

  for (const [cardId, card] of loserCardMap) {
    let stat = world.globalCardStats.get(cardId);
    if (!stat) {
      stat = {
        cardId,
        cardName: card.name,
        attribute: card.attribute || 'なし',
        cardType: card.type || 'monster',
        gamesUsed: 0,
        winsWithCard: 0,
      };
      world.globalCardStats.set(cardId, stat);
    }
    stat.gamesUsed++;
    // 敗北なのでwinsWithCardは増やさない
  }
}

/**
 * 日別スナップショットを保存
 */
export function takeDailySnapshot(world: SimWorld): void {
  // 現在の統計をディープコピー
  const statsCopy = new Map<string, GlobalCardStat>();
  for (const [cardId, stat] of world.globalCardStats) {
    statsCopy.set(cardId, { ...stat });
  }

  const snapshot: DailyCardSnapshot = {
    day: world.day,
    stats: statsCopy,
    totalGames: world.totalGamesPlayed,
  };

  world.dailySnapshots.push(snapshot);

  // グループ統計を更新（グループモード時）
  updateGroupStats(world);
}

/**
 * グループ統計を更新
 * レーティング平均・最大・最小、人口統計を再計算
 */
export function updateGroupStats(world: SimWorld): void {
  if (!world.groupStats || !world.groupModeConfig?.enabled) return;

  // 各グループの統計をリセット（累積値以外）
  for (const stats of world.groupStats.values()) {
    stats.aliveCount = 0;
    stats.partnerCount = 0;
    stats.averageRating = 0;
    stats.maxRating = 0;
    stats.minRating = Infinity;
  }

  // NPCをカウント
  for (const npc of world.npcs) {
    if (!npc.group || !npc.isAlive) continue;

    const stats = world.groupStats.get(npc.group);
    if (!stats) continue;

    stats.aliveCount++;
    stats.averageRating += npc.rating;
    stats.maxRating = Math.max(stats.maxRating, npc.rating);
    stats.minRating = Math.min(stats.minRating, npc.rating);

    if (npc.hasPartner) {
      stats.partnerCount++;
    }
  }

  // 平均を計算
  for (const stats of world.groupStats.values()) {
    if (stats.aliveCount > 0) {
      stats.averageRating = Math.round(stats.averageRating / stats.aliveCount);
    } else {
      stats.averageRating = 1000;
      stats.maxRating = 1000;
      stats.minRating = 1000;
    }
    // minRatingがInfinityの場合は1000に設定
    if (stats.minRating === Infinity) {
      stats.minRating = 1000;
    }
  }
}

/**
 * グループモードが有効かどうかを判定
 */
export function isGroupModeEnabled(world: SimWorld): boolean {
  return !!(world.groupModeConfig?.enabled && world.groupModeConfig.groups.length > 0);
}

/**
 * NPCのグループを取得（グループなしモードの場合はundefined）
 */
export function getNPCGroup(npc: SimNPC, world: SimWorld): GroupId | undefined {
  if (!isGroupModeEnabled(world)) return undefined;
  return npc.group;
}

/**
 * 同じグループに属するかどうかを判定
 * グループなしモードの場合は常にtrue
 */
export function isSameGroup(npc1: SimNPC, npc2: SimNPC, world: SimWorld): boolean {
  if (!isGroupModeEnabled(world)) return true;
  return npc1.group === npc2.group;
}

/**
 * 異なるグループに属するかどうかを判定
 * グループなしモードの場合は常にtrue（全員対戦可能）
 */
export function isDifferentGroup(npc1: SimNPC, npc2: SimNPC, world: SimWorld): boolean {
  if (!isGroupModeEnabled(world)) return true;
  return npc1.group !== npc2.group;
}

// === 非対称進化モード用ヘルパー関数 ===

/**
 * NPCのグループ設定を取得
 */
export function getGroupConfigForNpc(npc: SimNPC, world: SimWorld): GroupConfig | undefined {
  if (!isGroupModeEnabled(world) || !npc.group) return undefined;
  return world.groupModeConfig?.groups.find(g => g.id === npc.group);
}

/**
 * NPCの戦闘設定を取得
 */
export function getBattleConfigForNpc(npc: SimNPC, world: SimWorld): BattleConfig | undefined {
  const groupConfig = getGroupConfigForNpc(npc, world);
  if (!groupConfig) return undefined;
  return getGroupBattleConfig(groupConfig);
}

/**
 * 非対称進化モードが有効かどうかを判定
 * いずれかのグループがbattleConfigまたはpopulationMode='fixed'を持っていれば有効
 */
export function isAsymmetricEvolutionEnabled(world: SimWorld): boolean {
  if (!isGroupModeEnabled(world)) return false;
  return world.groupModeConfig!.groups.some(
    g => g.battleConfig !== undefined || g.populationMode === 'fixed'
  );
}

/**
 * グループが固定人口モードかどうか
 */
export function isGroupFixedPopulation(groupId: GroupId, world: SimWorld): boolean {
  if (!isGroupModeEnabled(world)) return false;
  const group = world.groupModeConfig?.groups.find(g => g.id === groupId);
  return group?.populationMode === 'fixed';
}

/**
 * グループの固定人口数を取得
 */
export function getGroupFixedPopulation(groupId: GroupId, world: SimWorld): number | undefined {
  if (!isGroupModeEnabled(world)) return undefined;
  const group = world.groupModeConfig?.groups.find(g => g.id === groupId);
  if (group?.populationMode !== 'fixed') return undefined;
  return group.fixedPopulation ?? group.initialNpcCount;
}
